import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    IoArrowBack,
    IoSend,
    IoTrashOutline,
    IoDocumentTextOutline,
    IoAddCircleOutline,
    IoCloudUploadOutline,
    IoToggle,
    IoEllipsisVertical,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoClose,
} from "react-icons/io5";
import { HiOutlineTemplate } from "react-icons/hi";
import { Loader2, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { DataState } from "../../context/DataProvider";
import {
    listReportTemplates,
    deleteReportTemplate,
    toggleReportTemplateStatus,
    getReportTemplate,
    updateTemplateSection,
    insertTemplateSection,
    autoParseTemplate,
} from "../../services/reportTemplateApi";
import { uploadFiles, initializeStreamChat, streamChat, createTask } from "../../services/api";


// =========================================================================
// SectionEditForm — multi-field edit form for sections
// =========================================================================
const SectionEditForm = ({ initialTitle = "", initialInstruction = "", initialStyling = "", onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [title, setTitle] = useState(initialTitle);
    const [instruction, setInstruction] = useState(initialInstruction);
    const [styling, setStyling] = useState(initialStyling);
    const titleRef = useRef(null);

    useEffect(() => { titleRef.current?.focus(); }, []);

    const handleSubmit = () => {
        if (!title.trim() || isLoading) return;
        onSubmit({ title: title.trim(), instruction: instruction.trim(), styling: styling.trim() });
    };

    return (
        <div
            className="absolute left-1/2 -translate-x-1/2 z-30 w-[90%] max-w-[500px]"
            style={{ top: "100%" }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mt-2 flex flex-col gap-3 p-4 rounded-xl bg-white border border-indigo-300 shadow-xl shadow-indigo-100/40">
                <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{isEdit ? "Edit Section" : "New Section"}</span>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <IoClose className="text-sm" />
                    </button>
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Section Title</label>
                    <input
                        ref={titleRef}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
                            if (e.key === "Escape") onCancel();
                        }}
                        placeholder="e.g. Executive Summary"
                        className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                        disabled={isLoading}
                    />
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Content / Sub-Points for AI</label>
                    <textarea
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        placeholder="Add sub-points or data constraints for the AI..."
                        className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors resize-y min-h-[50px]"
                        disabled={isLoading}
                    />
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Formatting & Styling Requirements</label>
                    <input
                        value={styling}
                        onChange={(e) => setStyling(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
                        }}
                        placeholder="e.g., Use bullet points, bold headers, table format..."
                        className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none focus:border-indigo-400 transition-colors"
                        disabled={isLoading}
                    />
                </div>

                <div className="flex justify-end gap-2 mt-1">
                    <button
                        onClick={onCancel}
                        disabled={isLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!title.trim() || isLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-xs font-medium transition-colors"
                    >
                        {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <IoCheckmarkCircle className="text-sm" />}
                        {isEdit ? "Save Changes" : "Create Section"}
                    </button>
                </div>
            </div>
        </div>
    );
};


// =========================================================================
// Structural Template Preview Component
// =========================================================================
const TemplateStructuralPreview = ({ templateName, header, footer, sections }) => {
    return (
        <div className="flex justify-center py-8 px-4" onClick={(e) => e.stopPropagation()}>
            <div
                className="w-full max-w-[816px] bg-white rounded-sm shadow-2xl shadow-black/40 text-black flex flex-col overflow-hidden"
                style={{
                    minHeight: "1056px",
                    fontFamily: "'Inter', 'Roboto', sans-serif"
                }}
            >
                <div className="flex-1 p-[72px] flex flex-col gap-6">
                    {/* Header */}
                    {header && (
                        <div className="border border-dashed border-gray-300 rounded p-4 bg-gray-50/50 relative">
                            <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] uppercase font-bold text-gray-400 tracking-wider">Document Header</span>
                            {header.images && header.images.length > 0 && (
                                <div className="flex justify-between items-center mb-4">
                                    <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                                        [Logo]
                                    </div>
                                </div>
                            )}
                            <div className="text-sm text-gray-600">
                                {header.text ? header.text : <span className="italic text-gray-400">Header Content</span>}
                            </div>
                        </div>
                    )}

                    {/* Title */}
                    <div className="text-center mb-8 mt-4">
                        <h1 className="text-3xl font-bold text-gray-800">
                            <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md font-mono text-xl border border-indigo-100">
                                {`{{ Template: ${templateName || "Untitled"} }}`}
                            </span>
                        </h1>
                    </div>

                    {/* Sections */}
                    <div className="flex flex-col gap-8 flex-1">
                        {(!sections || sections.length === 0) ? (
                            <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                                <p className="text-gray-400 italic">No sections defined yet. Add some structure on the right.</p>
                            </div>
                        ) : (
                            sections.map((section, idx) => (
                                <div key={idx} className="group relative border border-gray-200 rounded-lg p-6 hover:border-indigo-300 hover:shadow-md transition-all bg-white">
                                    <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] uppercase font-bold text-indigo-400 tracking-wider">Section {idx + 1}</span>

                                    <h2 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="inline-block px-2 py-1 bg-indigo-50/80 text-indigo-700 rounded text-sm font-mono whitespace-normal leading-relaxed border border-indigo-100/50">
                                            {`{{ ${section.title || section.heading || 'Section Title'} }}`}
                                        </span>
                                    </h2>

                                    <div className="bg-gray-50 rounded-md p-4 border border-gray-100 min-h-[100px] flex flex-col gap-3 items-start">
                                        <p className="text-sm text-gray-500 font-mono leading-relaxed opacity-60">
                                            {`{{ AI Generated Text Context: ${section.description || section.content_summary || 'Detailed summary content for this section goes here.'} }}`}
                                        </p>
                                        {section.styling && (
                                            <div className="flex items-center gap-2 mt-auto pt-2 border-t border-gray-100 w-full">
                                                <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-tighter">Styling:</span>
                                                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[10px] text-indigo-600 font-medium border border-indigo-100/50 italic">
                                                    {section.styling}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {footer && (
                        <div className="border border-dashed border-gray-300 rounded p-4 bg-gray-50/50 relative mt-8">
                            <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] uppercase font-bold text-gray-400 tracking-wider">Document Footer</span>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                                <div>{footer.text ? footer.text : <span className="italic text-gray-400">Footer Text</span>}</div>
                                <div className="font-mono bg-gray-200 px-2 py-0.5 rounded text-gray-500 text-xs">
                                    {`{{ Page Number }}`}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// =========================================================================
// Main Component
// =========================================================================
const ReportTemplates = () => {
    const navigate = useNavigate();
    const { userGoogleId: accessToken } = DataState();

    // --- Template List State ---
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);

    // --- Preview State ---
    const [previewSections, setPreviewSections] = useState([]);
    const [templateHeader, setTemplateHeader] = useState(null);
    const [templateFooter, setTemplateFooter] = useState(null);
    const [templateId, setTemplateId] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [htmlPreview, setHtmlPreview] = useState("");

    // --- Inline Edit State ---
    const [activeSectionIndex, setActiveSectionIndex] = useState(null);
    const [insertAtIndex, setInsertAtIndex] = useState(null);
    const [inlineLoading, setInlineLoading] = useState(false);

    // --- Upload State ---
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // --- Chat State (for upload parsing) ---
    const [chatTaskId, setChatTaskId] = useState(null);

    // --- Load templates ---
    const fetchTemplates = useCallback(async () => {
        if (!accessToken) return;
        setLoadingTemplates(true);
        try {
            const data = await listReportTemplates(accessToken);
            setTemplates(data?.templates || []);
        } catch (err) {
            console.error("Failed to load templates:", err);
        } finally {
            setLoadingTemplates(false);
        }
    }, [accessToken]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    // --- Load full template when selected from sidebar ---
    const loadTemplatePreview = useCallback(async (template) => {
        setSelectedTemplate(template);
        setActiveSectionIndex(null);
        setInsertAtIndex(null);
        try {
            const full = await getReportTemplate(accessToken, template._id);
            setPreviewSections(full.sections || []);
            setTemplateHeader(full.header || null);
            setTemplateFooter(full.footer || null);
            setTemplateId(full._id);
            setTemplateName(full.template_name || "");
            setHtmlPreview(full.html_preview || "");
        } catch (err) {
            console.error("Failed to load template:", err);
            toast.error("Failed to load template");
        }
    }, [accessToken]);

    // --- Handle file upload → parse via direct auto-parse API (no LLM agent) ---
    const handleUpload = async (file) => {
        if (!file) {
            toast.error("No file detected by the browser.");
            return;
        }
        if (!file.name.toLowerCase().endsWith(".docx")) {
            toast.error(`Invalid file type: ${file.name}. Please upload a .docx file`);
            return;
        }
        setIsUploading(true);
        setPreviewSections([]);
        setTemplateHeader(null);
        setTemplateFooter(null);
        setTemplateId(null);
        setHtmlPreview("");
        setSelectedTemplate(null);

        toast.success("Starting upload and parsing process...");

        try {
            const result = await autoParseTemplate(accessToken, file);

            if (result?.error) {
                toast.error(result.error);
                setIsUploading(false);
                return;
            }

            toast.success(result?.message || "Template parsed and saved!");

            // Refresh template list and load the newly created template
            await fetchTemplates();
            if (result?.template_id) {
                try {
                    const full = await getReportTemplate(accessToken, result.template_id);
                    setPreviewSections(full.sections || []);
                    setTemplateHeader(full.header || null);
                    setTemplateFooter(full.footer || null);
                    setTemplateId(full._id);
                    setTemplateName(full.template_name || "");
                    setHtmlPreview(full.html_preview || "");
                    setSelectedTemplate(full);
                } catch (err) {
                    console.error("Failed to load new template:", err);
                    // Fallback: load the first template from the list
                    const data = await listReportTemplates(accessToken);
                    const all = data?.templates || [];
                    if (all.length > 0) loadTemplatePreview(all[0]);
                }
            } else {
                // Fallback: load the first template from the list
                const data = await listReportTemplates(accessToken);
                const all = data?.templates || [];
                if (all.length > 0) loadTemplatePreview(all[0]);
            }
        } catch (err) {
            console.error("Upload/parse error:", err);
            toast.error("Failed to upload and parse template.");
        } finally {
            setIsUploading(false);
        }
    };

    // --- Drag and Drop ---
    const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
    const handleDragLeave = () => setIsDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleUpload(file);
    };

    // --- Section click ---
    const handleSectionClick = (index) => {
        setInsertAtIndex(null);
        setActiveSectionIndex(activeSectionIndex === index ? null : index);
    };

    // --- Divider click ---
    const handleDividerClick = (index, e) => {
        e.stopPropagation();
        setActiveSectionIndex(null);
        setInsertAtIndex(insertAtIndex === index ? null : index);
    };

    // --- Helper to stream AI updates ---
    const streamAITemplateUpdate = async (promptMessage, successMessage) => {
        try {
            let taskId = chatTaskId;
            if (!taskId) {
                const taskData = await createTask({
                    task: `Template Builder: Update Template`,
                    agent_sequence: ["TemplateBuilder"],
                }, accessToken);
                taskId = taskData._id || taskData.task_id;
                setChatTaskId(taskId);
            }

            const chatsPayload = [{
                role: "user",
                content: [{ type: "text", text: promptMessage }]
            }];
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const initResponse = await initializeStreamChat({
                chats: chatsPayload,
                task_id: taskId,
                is_agent_mode: true,
                agent_sequence: ["TemplateBuilder"],
                timezone,
            }, accessToken);

            const eventSource = streamChat(initResponse.stream_id, accessToken);
            let streamEnded = false;

            return new Promise((resolve, reject) => {
                eventSource.addEventListener("streamEnd", () => {
                    if (streamEnded) return;
                    streamEnded = true;
                    eventSource.close();
                    setTimeout(async () => {
                        await fetchTemplates();
                        if (templateId) {
                            try {
                                const full = await getReportTemplate(accessToken, templateId);
                                setPreviewSections(full.sections || []);
                                setTemplateHeader(full.header || null);
                                setTemplateFooter(full.footer || null);
                            } catch (err) {
                                console.error("Failed to reload template", err);
                            }
                        }
                        toast.success(successMessage);
                        resolve();
                    }, 1000);
                });

                eventSource.onerror = () => {
                    if (streamEnded) return;
                    streamEnded = true;
                    eventSource.close();
                    toast.error("Error updating template via AI.");
                    reject(new Error("Stream error"));
                };
            });
        } catch (err) {
            console.error("AI Update Error:", err);
            toast.error("Failed to update template via AI");
            throw err;
        }
    };

    // --- Submit section edit ---
    const handleSectionEdit = async ({ title, instruction, styling }) => {
        if (!templateId || activeSectionIndex === null) return;
        setInlineLoading(true);
        try {
            const cur = previewSections[activeSectionIndex];
            const promptMessage = `I am modifying my report template (ID: ${templateId}).
I want to EDIT the section at index ${activeSectionIndex} (Current Title: "${cur.title || cur.heading}").

User Instructions for AI for this section:
New Title: "${title}"
Content Points: "${instruction}"
Formatting & Styling Requirements: "${styling || 'None specified'}"

Please understand these instructions, optimize the section title and description if necessary (incorporating BOTH the content points and styling requirements into the saved section description) to make it a great report template section, and use the \`update_report_template\` tool to save the changes. 

IMPORTANT: The \`update_report_template\` tool requires the FULL list of sections to replace the existing ones. You MUST include all other existing sections unmodified in your tool call array, and only modify the one at index ${activeSectionIndex}. 
Here is the current full list of sections for your reference:
${JSON.stringify(previewSections, null, 2)}`;

            await streamAITemplateUpdate(promptMessage, "Section updated via AI!");
            setActiveSectionIndex(null);
        } catch (err) {
            console.error("Section update error:", err);
        } finally {
            setInlineLoading(false);
        }
    };

    // --- Submit section insert ---
    const handleSectionInsert = async ({ title, instruction, styling }) => {
        if (!templateId || insertAtIndex === null) return;
        setInlineLoading(true);
        try {
            const promptMessage = `I am modifying my report template (ID: ${templateId}).
I want to INSERT a new section at index ${insertAtIndex}.

User Instructions for AI for this NEW section:
Title: "${title}"
Content Points: "${instruction}"
Formatting & Styling Requirements: "${styling || 'None specified'}"

Please understand these instructions, optimize the section title and description if necessary (incorporating BOTH the content points and styling requirements into the saved section description) to make it a great report template section, and use the \`update_report_template\` tool to save the changes. 

IMPORTANT: The \`update_report_template\` tool requires the FULL list of sections to replace the existing ones. You MUST include all other existing sections unmodified in your tool call array, and insert the new section at index ${insertAtIndex}. 
Here is the current full list of sections before insertion for your reference:
${JSON.stringify(previewSections, null, 2)}`;

            await streamAITemplateUpdate(promptMessage, "Section inserted via AI!");
            setInsertAtIndex(null);
        } catch (err) {
            console.error("Section insert error:", err);
        } finally {
            setInlineLoading(false);
        }
    };

    // --- Delete ---
    const handleDelete = async (tid) => {
        try {
            await deleteReportTemplate(accessToken, tid);
            toast.success("Template deleted");
            fetchTemplates();
            setMenuOpenId(null);
            if (selectedTemplate?._id === tid) {
                setSelectedTemplate(null);
                setPreviewSections([]);
                setTemplateId(null);
                setHtmlPreview("");
            }
        } catch (err) { toast.error("Failed to delete template"); }
    };

    // --- Toggle ---
    const handleToggleStatus = async (tid) => {
        try {
            const result = await toggleReportTemplateStatus(accessToken, tid);
            toast.success(result.message || "Status toggled");
            fetchTemplates();
            setMenuOpenId(null);
        } catch (err) { toast.error("Failed to toggle status"); }
    };

    // --- New template ---
    const handleNewTemplate = () => {
        setSelectedTemplate(null);
        setPreviewSections([]);
        setTemplateHeader(null);
        setTemplateFooter(null);
        setTemplateId(null);
        setTemplateName("");
        setHtmlPreview("");
        setActiveSectionIndex(null);
        setInsertAtIndex(null);
    };

    // --- Dismiss inline editors when clicking outside ---
    const handleDocumentPageClick = () => {
        setActiveSectionIndex(null);
        setInsertAtIndex(null);
    };

    const hasPreview = previewSections.length > 0 || !!htmlPreview;

    // =====================================================================
    // RENDER
    // =====================================================================
    return (
        <div className="flex w-full h-screen bg-[#0a0a0c] text-zinc-200 overflow-hidden">
            <style>
                {`
                .mammoth-preview p { margin-bottom: 1em; line-height: 1.6; }
                .mammoth-preview h1, .mammoth-preview h2, .mammoth-preview h3, .mammoth-preview h4 { margin-top: 1.5em; margin-bottom: 0.5em; font-weight: bold; }
                .mammoth-preview h1 { font-size: 2em; }
                .mammoth-preview h2 { font-size: 1.5em; }
                .mammoth-preview table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
                .mammoth-preview td, .mammoth-preview th { border: 1px solid #ddd; padding: 8px; }
                .mammoth-preview img { max-width: 100%; height: auto; display: block; margin: 1em auto; }
                .mammoth-preview ul, .mammoth-preview ol { margin-left: 2em; margin-bottom: 1em; }
                .mammoth-preview li { margin-bottom: 0.5em; }
                `}
            </style>

            {/* ======================== LEFT SIDEBAR ======================== */}
            <div className="flex flex-col w-72 h-full border-r border-zinc-800 bg-[#111114] flex-shrink-0">
                <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-800">
                    <div className="flex items-center gap-2">
                        <button onClick={() => navigate("/chat")} className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors" title="Back to Chat">
                            <IoArrowBack className="text-lg text-zinc-400" />
                        </button>
                        <h2 className="text-sm font-bold text-zinc-100">Report Templates</h2>
                    </div>
                    <button onClick={handleNewTemplate} className="p-1.5 rounded-lg bg-[#5652CF]/20 hover:bg-[#5652CF]/30 border border-[#5652CF]/30 transition-all" title="New Template">
                        <IoAddCircleOutline className="text-lg text-[#5652CF]" />
                    </button>
                </div>

                <div className="flex-1 p-3 space-y-2 overflow-y-auto custom-scrollbar">
                    {loadingTemplates ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-5 h-5 text-[#5652CF] animate-spin" />
                            <p className="mt-2 text-xs text-zinc-500">Loading templates...</p>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <HiOutlineTemplate className="w-10 h-10 text-zinc-700 mb-3" />
                            <p className="text-sm text-zinc-400 font-medium">No templates yet</p>
                            <p className="text-xs text-zinc-600 mt-1">Upload a .docx to get started</p>
                        </div>
                    ) : (
                        templates.map((template) => (
                            <div
                                key={template._id}
                                className={`group relative p-3 rounded-xl border transition-all duration-200 cursor-pointer ${selectedTemplate?._id === template._id
                                    ? "bg-[#5652CF]/10 border-[#5652CF]/30"
                                    : "bg-zinc-900/50 border-zinc-800/50 hover:bg-zinc-800/50 hover:border-zinc-700"
                                    }`}
                                onClick={() => loadTemplatePreview(template)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <IoDocumentTextOutline className="text-sm text-zinc-400 flex-shrink-0" />
                                            <h3 className="text-sm font-semibold text-zinc-200 truncate">{template.template_name}</h3>
                                        </div>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700">
                                                {template.template_type?.replace("_", " ")}
                                            </span>
                                            <span className="text-[10px] text-zinc-600">{template.section_count} sections</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-2">
                                            {template.is_active ? (
                                                <span className="flex items-center gap-1 text-[10px] text-emerald-400"><IoCheckmarkCircle className="text-xs" /> Active</span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-[10px] text-zinc-500"><IoCloseCircle className="text-xs" /> Inactive</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <button onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === template._id ? null : template._id); }} className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-zinc-700 transition-all">
                                            <IoEllipsisVertical className="text-sm text-zinc-400" />
                                        </button>
                                        {menuOpenId === template._id && (
                                            <div className="absolute right-0 top-8 z-20 w-40 py-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl">
                                                <button onClick={(e) => { e.stopPropagation(); handleToggleStatus(template._id); }} className="w-full px-3 py-2 text-left text-xs hover:bg-zinc-700 flex items-center gap-2">
                                                    <IoToggle className="text-sm" />{template.is_active ? "Deactivate" : "Activate"}
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); handleDelete(template._id); }} className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-zinc-700 flex items-center gap-2">
                                                    <IoTrashOutline className="text-sm" />Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* =================== CENTER: DOCUMENT PREVIEW =================== */}
            <div className="flex flex-col flex-1 h-full min-w-0 bg-[#1a1a20]">
                {/* Top bar */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-800 bg-[#111114]">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#5652CF] to-indigo-500 flex items-center justify-center shadow-lg shadow-[#5652CF]/20">
                            <HiOutlineTemplate className="text-white text-sm" />
                        </div>
                        <div>
                            <h1 className="text-sm font-bold text-zinc-100">{templateName || "Template Preview"}</h1>
                            <p className="text-[10px] text-zinc-500">
                                {hasPreview ? "Visual Preview of the Document" : "Upload a .docx file to get started"}
                            </p>
                        </div>
                    </div>
                    {hasPreview && (
                        <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-xs text-zinc-300 transition-colors">
                            <IoCloudUploadOutline className="text-sm" />Upload New
                        </button>
                    )}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar" style={{ background: "#2a2a32" }}>
                    {!hasPreview && !isUploading ? (
                        /* ========== UPLOAD EMPTY STATE ========== */
                        <div className="flex flex-col items-center justify-center h-full px-6">
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`w-full max-w-lg p-12 rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer group ${isDragOver
                                    ? "border-[#5652CF] bg-[#5652CF]/10 scale-[1.02]"
                                    : "border-zinc-600 hover:border-[#5652CF]/50 hover:bg-zinc-800/30"
                                    }`}
                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all ${isDragOver ? "bg-[#5652CF]/20 border border-[#5652CF]/30" : "bg-zinc-800 border border-zinc-700 group-hover:bg-[#5652CF]/10"
                                        }`}>
                                        <IoCloudUploadOutline className={`text-3xl transition-colors ${isDragOver ? "text-[#5652CF]" : "text-zinc-400 group-hover:text-[#5652CF]"}`} />
                                    </div>
                                    <h2 className="text-lg font-bold text-zinc-100 mb-2">Upload a Report Template</h2>
                                    <p className="text-sm text-zinc-500 max-w-sm mb-4">
                                        Drag and drop a .docx file here, or click to browse. We'll parse the exact styling and structure.
                                    </p>
                                    <span className="text-xs px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-400">.docx files only</span>
                                </div>
                            </div>
                        </div>
                    ) : isUploading ? (
                        /* ========== PARSING STATE ========== */
                        <div className="flex flex-col items-center justify-center h-full px-6">
                            <Loader2 className="w-10 h-10 text-[#5652CF] animate-spin mb-4" />
                            <h2 className="text-lg font-bold text-zinc-100 mb-2">Parsing Template...</h2>
                            <p className="text-sm text-zinc-500">Extracting exact HTML styling and structural sections</p>
                        </div>
                    ) : (
                        /* ========== VISUAL HTML PREVIEW ========== */
                        <TemplateStructuralPreview
                            templateName={templateName}
                            header={templateHeader}
                            footer={templateFooter}
                            sections={previewSections}
                        />
                    )}
                    {/* Hidden input kept outside conditionals so ref is always valid */}
                    <input ref={fileInputRef} type="file" accept=".docx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
                </div>
            </div>

            {/* ======================== RIGHT SIDEBAR: STRUCTURE ======================== */}
            {hasPreview && (
                <div className="flex flex-col w-[350px] h-full border-l border-zinc-800 bg-[#111114] flex-shrink-0">
                    <div className="px-5 py-4 border-b border-zinc-800 shadow-sm z-10">
                        <h2 className="text-sm font-bold text-zinc-100">Template Structure</h2>
                        <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                            Interact with the section structure below. Click to edit instructions for generating each section.
                        </p>
                    </div>

                    <div className="flex-1 p-5 overflow-y-auto custom-scrollbar bg-[#0c0c0f]">
                        {/* Insert top divider */}
                        <div className="relative group/divider" style={{ padding: "8px 0" }}>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/divider:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => handleDividerClick(0, e)}
                                    className={`flex items-center justify-center w-6 h-6 rounded-full border transition-all ${insertAtIndex === 0
                                        ? "bg-[#5652CF] border-[#5652CF] text-white shadow-lg shadow-[#5652CF]/20"
                                        : "bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-[#5652CF] hover:text-[#5652CF]"
                                        }`}
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                            </div>
                            {insertAtIndex === 0 && (
                                <SectionEditForm
                                    initialTitle=""
                                    initialInstruction=""
                                    isEdit={false}
                                    onSubmit={handleSectionInsert}
                                    onCancel={() => setInsertAtIndex(null)}
                                    isLoading={inlineLoading}
                                />
                            )}
                        </div>

                        {previewSections.map((section, idx) => (
                            <React.Fragment key={idx}>
                                {/* Interactive Section Card */}
                                <div
                                    onClick={(e) => { e.stopPropagation(); handleSectionClick(idx); }}
                                    className={`relative p-4 rounded-xl border transition-all duration-200 cursor-pointer ${activeSectionIndex === idx
                                        ? "bg-[#5652CF]/10 border-[#5652CF]/50 shadow-lg shadow-[#5652CF]/10"
                                        : "bg-zinc-900/60 border-zinc-800/80 hover:bg-zinc-800/80 hover:border-zinc-700"
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${activeSectionIndex === idx
                                            ? "bg-[#5652CF]/20 text-[#5652CF] border border-[#5652CF]/30"
                                            : "bg-zinc-800 text-zinc-500 border border-zinc-700"
                                            }`}>
                                            {idx + 1}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <h3 className={`text-sm font-semibold mb-1 ${activeSectionIndex === idx ? "text-[#5652CF]" : "text-zinc-200"}`}>
                                                {section.title || section.heading || `Section ${idx + 1}`}
                                            </h3>
                                            {(section.description || section.content_summary) && (
                                                <p className="text-[11px] text-zinc-500 leading-relaxed max-h-20 overflow-hidden line-clamp-3">
                                                    {section.description || section.content_summary}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Inline Chat for Editing */}
                                    {activeSectionIndex === idx && (
                                        <div className="mt-3 relative z-20">
                                            <SectionEditForm
                                                initialTitle={section.title || section.heading}
                                                initialInstruction={section.description || section.content_summary}
                                                initialStyling={section.styling || ""}
                                                isEdit={true}
                                                onSubmit={handleSectionEdit}
                                                onCancel={() => setActiveSectionIndex(null)}
                                                isLoading={inlineLoading}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Divider below section */}
                                <div className="relative group/divider" style={{ padding: "8px 0" }}>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/divider:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => handleDividerClick(idx + 1, e)}
                                            className={`flex items-center justify-center w-6 h-6 rounded-full border transition-all ${insertAtIndex === idx + 1
                                                ? "bg-[#5652CF] border-[#5652CF] text-white shadow-lg shadow-[#5652CF]/20"
                                                : "bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-[#5652CF] hover:text-[#5652CF]"
                                                }`}
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                    {insertAtIndex === idx + 1 && (
                                        <SectionEditForm
                                            initialTitle=""
                                            initialInstruction=""
                                            isEdit={false}
                                            onSubmit={handleSectionInsert}
                                            onCancel={() => setInsertAtIndex(null)}
                                            isLoading={inlineLoading}
                                        />
                                    )}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {/* Close menu overlay */}
            {menuOpenId && <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />}

            {/* Hidden file input for "Upload New" */}
            {hasPreview && (
                <input ref={fileInputRef} type="file" accept=".docx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }} />
            )}
        </div>
    );
};

export default ReportTemplates;
